import React, { useState, useEffect } from 'react';
import { 
  Truck, 
  Save, 
  Plus,
  Edit,
  Trash2,
  MapPin,
  DollarSign,
  Package,
  AlertCircle
} from 'lucide-react';
import api from '../../utils/api';

interface ShippingZone {
  _id: string;
  name: string;
  states: string[];
  shippingRate: number;
  expressRate: number;
}

interface ShippingSettings {
  _id: string;
  freeShipping: boolean;
  freeShippingThreshold: number;
  standardShippingRate: number;
  expressShippingRate: number;
  codCharges: number;
  enableCodCharges: boolean;
  shippingZones: ShippingZone[];
  lastUpdated: string;
}

const ShippingManagement: React.FC = () => {
  const [settings, setSettings] = useState<ShippingSettings | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [showZoneForm, setShowZoneForm] = useState(false);
  const [editingZone, setEditingZone] = useState<ShippingZone | null>(null);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const [zoneForm, setZoneForm] = useState({
    name: '',
    states: '',
    shippingRate: '',
    expressRate: ''
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setIsLoading(true);
      const response = await api.get('/shipping/settings');
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching shipping settings:', error);
      setError('Failed to load shipping settings');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    if (!settings) return;

    try {
      setIsSaving(true);
      setError('');
      const response = await api.put('/shipping/settings', {
        freeShipping: settings.freeShipping,
        freeShippingThreshold: settings.freeShippingThreshold,
        standardShippingRate: settings.standardShippingRate,
        expressShippingRate: settings.expressShippingRate,
        codCharges: settings.codCharges,
        enableCodCharges: settings.enableCodCharges
      });
      
      setSettings(response.data.settings);
      setMessage('Shipping settings saved successfully!');
      setTimeout(() => setMessage(''), 3000);
    } catch (error: any) {
      console.error('Error saving settings:', error);
      setError(error.response?.data?.message || 'Failed to save settings');
    } finally {
      setIsSaving(false);
    }
  };

  const handleZoneSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setError('');
      const zoneData = {
        name: zoneForm.name,
        states: zoneForm.states.split(',').map(s => s.trim()).filter(s => s),
        shippingRate: Number(zoneForm.shippingRate),
        expressRate: Number(zoneForm.expressRate)
      };

      let response;
      if (editingZone) {
        response = await api.put(`/shipping/zones/${editingZone._id}`, zoneData);
      } else {
        response = await api.post('/shipping/zones', zoneData);
      }

      setSettings(response.data.settings);
      resetZoneForm();
      setMessage(`Shipping zone ${editingZone ? 'updated' : 'added'} successfully!`);
      setTimeout(() => setMessage(''), 3000);
    } catch (error: any) {
      console.error('Error saving zone:', error);
      setError(error.response?.data?.message || 'Failed to save shipping zone');
    }
  };

  const handleDeleteZone = async (zoneId: string) => {
    if (!window.confirm('Are you sure you want to delete this shipping zone?')) {
      return;
    }

    try {
      const response = await api.delete(`/shipping/zones/${zoneId}`);
      setSettings(response.data.settings);
      setMessage('Shipping zone deleted successfully!');
      setTimeout(() => setMessage(''), 3000);
    } catch (error: any) {
      console.error('Error deleting zone:', error);
      setError(error.response?.data?.message || 'Failed to delete shipping zone');
    }
  };

  const startEditZone = (zone: ShippingZone) => {
    setEditingZone(zone);
    setZoneForm({
      name: zone.name,
      states: zone.states.join(', '),
      shippingRate: zone.shippingRate.toString(),
      expressRate: zone.expressRate.toString()
    });
    setShowZoneForm(true);
  };

  const resetZoneForm = () => {
    setZoneForm({
      name: '',
      states: '',
      shippingRate: '',
      expressRate: ''
    });
    setEditingZone(null);
    setShowZoneForm(false);
  };

  if (isLoading) {
    return (
      <div className="p-6 text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-4"></div>
        <p className="text-gray-600 dark:text-gray-400">Loading shipping settings...</p>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center">
          <Truck className="h-7 w-7 mr-3 text-primary-600" />
          Shipping Management
        </h2>
        <p className="text-gray-600 dark:text-gray-400 mt-1">
          Configure shipping rates, free shipping thresholds, and delivery zones
        </p>
      </div>

      {/* Messages */}
      {message && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 
                      text-green-700 dark:text-green-400 px-4 py-3 rounded-md mb-6">
          {message}
        </div>
      )}
      
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 
                      text-red-700 dark:text-red-400 px-4 py-3 rounded-md mb-6 flex items-center">
          <AlertCircle className="h-5 w-5 mr-2" />
          {error}
        </div>
      )}

      {settings && (
        <div className="space-y-6">
          {/* General Shipping Settings */}
          <div className="bg-white dark:bg-secondary-800 rounded-lg shadow-sm border border-gray-200 dark:border-secondary-700 p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center">
              <Package className="h-5 w-5 mr-2" />
              General Settings
            </h3>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Free Shipping */}
              <div className="space-y-4">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="freeShipping"
                    checked={settings.freeShipping}
                    onChange={(e) => setSettings({...settings, freeShipping: e.target.checked})}
                    className="mr-3 w-4 h-4 text-primary-600 bg-white dark:bg-secondary-700 
                             border-gray-300 dark:border-secondary-600 rounded 
                             focus:ring-primary-500"
                  />
                  <label htmlFor="freeShipping" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Enable Free Shipping
                  </label>
                </div>

                {settings.freeShipping && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Free Shipping Threshold (₹)
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={settings.freeShippingThreshold}
                      onChange={(e) => setSettings({...settings, freeShippingThreshold: Number(e.target.value)})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-secondary-600 
                               bg-white dark:bg-secondary-700 text-gray-900 dark:text-gray-100
                               rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="999"
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Orders above this amount get free shipping
                    </p>
                  </div>
                )}
              </div>

              {/* Standard Rates */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Standard Shipping Rate (₹)
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={settings.standardShippingRate}
                    onChange={(e) => setSettings({...settings, standardShippingRate: Number(e.target.value)})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-secondary-600 
                             bg-white dark:bg-secondary-700 text-gray-900 dark:text-gray-100
                             rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Express Shipping Rate (₹)
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={settings.expressShippingRate}
                    onChange={(e) => setSettings({...settings, expressShippingRate: Number(e.target.value)})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-secondary-600 
                             bg-white dark:bg-secondary-700 text-gray-900 dark:text-gray-100
                             rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>
            </div>

            {/* COD Settings */}
            <div className="mt-6 pt-6 border-t border-gray-200 dark:border-secondary-600">
              <div className="flex items-center mb-4">
                <input
                  type="checkbox"
                  id="enableCodCharges"
                  checked={settings.enableCodCharges}
                  onChange={(e) => setSettings({...settings, enableCodCharges: e.target.checked})}
                  className="mr-3 w-4 h-4 text-primary-600 bg-white dark:bg-secondary-700 
                           border-gray-300 dark:border-secondary-600 rounded 
                           focus:ring-primary-500"
                />
                <label htmlFor="enableCodCharges" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Enable Cash on Delivery Charges
                </label>
              </div>

              {settings.enableCodCharges && (
                <div className="max-w-xs">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    COD Charges (₹)
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={settings.codCharges}
                    onChange={(e) => setSettings({...settings, codCharges: Number(e.target.value)})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-secondary-600 
                             bg-white dark:bg-secondary-700 text-gray-900 dark:text-gray-100
                             rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              )}
            </div>

            {/* Save Button */}
            <div className="mt-6 pt-6 border-t border-gray-200 dark:border-secondary-600">
              <button
                onClick={handleSaveSettings}
                disabled={isSaving}
                className="bg-primary-600 hover:bg-primary-700 disabled:bg-gray-400 
                         text-white px-6 py-2 rounded-lg font-medium flex items-center 
                         transition-colors"
              >
                <Save className="h-4 w-4 mr-2" />
                {isSaving ? 'Saving...' : 'Save Settings'}
              </button>
            </div>
          </div>

          {/* Shipping Zones */}
          <div className="bg-white dark:bg-secondary-800 rounded-lg shadow-sm border border-gray-200 dark:border-secondary-700 p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center">
                <MapPin className="h-5 w-5 mr-2" />
                Shipping Zones
              </h3>
              <button
                onClick={() => setShowZoneForm(true)}
                className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 
                         rounded-lg font-medium flex items-center transition-colors"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Zone
              </button>
            </div>

            {/* Zone Form */}
            {showZoneForm && (
              <form onSubmit={handleZoneSubmit} className="mb-6 p-4 bg-gray-50 dark:bg-secondary-700 rounded-lg">
                <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-4">
                  {editingZone ? 'Edit Shipping Zone' : 'Add New Shipping Zone'}
                </h4>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Zone Name
                    </label>
                    <input
                      type="text"
                      required
                      value={zoneForm.name}
                      onChange={(e) => setZoneForm({...zoneForm, name: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-secondary-600 
                               bg-white dark:bg-secondary-700 text-gray-900 dark:text-gray-100
                               rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="e.g., Metro Cities"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      States (comma separated)
                    </label>
                    <input
                      type="text"
                      required
                      value={zoneForm.states}
                      onChange={(e) => setZoneForm({...zoneForm, states: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-secondary-600 
                               bg-white dark:bg-secondary-700 text-gray-900 dark:text-gray-100
                               rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="Delhi, Mumbai, Bangalore"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Standard Rate (₹)
                    </label>
                    <input
                      type="number"
                      min="0"
                      required
                      value={zoneForm.shippingRate}
                      onChange={(e) => setZoneForm({...zoneForm, shippingRate: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-secondary-600 
                               bg-white dark:bg-secondary-700 text-gray-900 dark:text-gray-100
                               rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Express Rate (₹)
                    </label>
                    <input
                      type="number"
                      min="0"
                      required
                      value={zoneForm.expressRate}
                      onChange={(e) => setZoneForm({...zoneForm, expressRate: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-secondary-600 
                               bg-white dark:bg-secondary-700 text-gray-900 dark:text-gray-100
                               rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                </div>

                <div className="flex space-x-3 mt-4">
                  <button
                    type="submit"
                    className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 
                             rounded-lg font-medium transition-colors"
                  >
                    {editingZone ? 'Update Zone' : 'Add Zone'}
                  </button>
                  <button
                    type="button"
                    onClick={resetZoneForm}
                    className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 
                             rounded-lg font-medium transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}

            {/* Zones List */}
            <div className="space-y-3">
              {settings.shippingZones.length > 0 ? (
                settings.shippingZones.map((zone) => (
                  <div key={zone._id} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-secondary-700 rounded-lg">
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-gray-100">{zone.name}</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        States: {zone.states.join(', ')}
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Standard: ₹{zone.shippingRate} | Express: ₹{zone.expressRate}
                      </p>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => startEditZone(zone)}
                        className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-900 
                                 dark:hover:text-indigo-300 p-2 rounded transition-colors"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteZone(zone._id)}
                        className="text-red-600 dark:text-red-400 hover:text-red-900 
                                 dark:hover:text-red-300 p-2 rounded transition-colors"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-gray-500 dark:text-gray-400 text-center py-8">
                  No shipping zones configured. Add a zone to set region-specific rates.
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShippingManagement;